import React, { useMemo } from 'react';
import classNames from 'classnames';
import { z } from 'zod';
import merge from 'lodash/merge';
import type { SchemaType } from '$util/schema';
import { CmsImageSchema, CmsLinkSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import type { AccordionCta, AccordionItem } from '$quantum/molecules/Accordion';
import { Accordion } from '$quantum/molecules/Accordion';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import {
  SideBySideMediaBaseSchema,
  defaultConfiguration as baseDefaultConfiguration,
} from '../SideBySideMediaBase/schema';
import { SideBySideMediaBase } from '../SideBySideMediaBase';
import styles from './index.module.scss';

export const defaultConfiguration = {
  ...baseDefaultConfiguration,
  headingsPosition: 'top-center' as const,
  openFirstItem: true,
  textRatio: 6,
};

export const SideBySideMediaAccordionSchema = {
  translatedEntities: SideBySideMediaBaseSchema.translatedEntities.extend({
    accordionItemTitle: z.record(CmsShortTextSchema),
    accordionItemContent: z.record(CmsRichTextSchema),
    accordionItemIcon: z.record(CmsImageSchema).optional(),
    accordionCtaTitle: CmsShortTextSchema.optional(),
    accordionCtaLink: CmsLinkSchema.optional(),
  }),
  configuration: SideBySideMediaBaseSchema.configuration
    .removeDefault()
    .extend({
      /**
       * @default true
       */
      openFirstItem: z.boolean().optional(),
    })
    .default(defaultConfiguration),
};

export type SideBySideMediaAccordionTranslatedEntities = SchemaType<
  (typeof SideBySideMediaAccordionSchema)['translatedEntities']
>;
export type SideBySideMediaAccordionConfiguration = SchemaType<
  (typeof SideBySideMediaAccordionSchema)['configuration']
>;

/**
 * Component that renders Media and Accordion columns.<br />
 * https://www.notion.so/fixter/SideBySideMediaAccordion-e676df5b6a894f07a13dbc9d9f35ca07
 */
export const SideBySideMediaAccordion: ContentComponent<
  SideBySideMediaAccordionTranslatedEntities,
  SideBySideMediaAccordionConfiguration
> = function SideBySideMediaAccordion({ translatedEntities, configuration, className, ...props }) {
  const {
    accordionItemTitle,
    accordionItemContent,
    accordionItemIcon,
    accordionCtaTitle,
    accordionCtaLink,
    ...translatedEntitiesBase
  } = translatedEntities;

  const { openFirstItem, ...configurationBase } = merge(defaultConfiguration, configuration);

  const accordionItems = useMemo<AccordionItem[]>(
    () =>
      Object.entries(accordionItemTitle).map(([index, itemTitle]) => {
        const title = itemTitle.value;
        const content = renderRichTextContent(accordionItemContent[index].content);
        const icon = accordionItemIcon?.[index];
        return { title, content, icon };
      }),
    [accordionItemContent, accordionItemIcon, accordionItemTitle]
  );

  const accordionCta = useMemo<AccordionCta | undefined>(() => {
    if (!accordionCtaLink) return undefined;
    return {
      ...accordionCtaLink,
      title: accordionCtaTitle?.value,
      backgroundColor: configurationBase.colors?.ctaBackgroundColor || styles.neutral100,
    };
  }, [configurationBase.colors?.ctaBackgroundColor, accordionCtaLink, accordionCtaTitle?.value]);

  return (
    <SideBySideMediaBase
      className={classNames(SideBySideMediaAccordion.displayName, className)}
      translatedEntities={translatedEntitiesBase}
      configuration={configurationBase}
      {...props}
    >
      <Accordion accordionItems={accordionItems} cta={accordionCta} openFirstItem={openFirstItem} />
    </SideBySideMediaBase>
  );
};

SideBySideMediaAccordion.displayName = 'SideBySideMediaAccordion';
SideBySideMediaAccordion.schema = SideBySideMediaAccordionSchema;
